import { isEmpty, uniq } from 'lodash-es';

import { format, Unit } from '@dynatrace-sdk/units';

import { formatDateTicks } from './format-date-ticks.js';
import {
  FormatterType,
  formatByType,
  formatterOptions,
} from '../../../formatters/format-value-by-type.js';
import { Formatter, type FormatterWithPrecision } from '../types/formatter.js';
import { TickValueFormatter, TickPrimitiveValue } from '../types/tick-value.js';

/* Chart Y axes & magnifiers precision used to adjust the fraction digits of the passed number */
export const DEFAULT_PRECISION = 1;
/* This is the max precision allowed in our charts for compatibility reasons with older browsers. */
export const MAX_PRECISION_ALLOWED = 20;

/**
 * Formats a value to a specified unit.
 * If the unit received is not in the formatterOptions then it is considered a custom unit.
 * @param value - The value to format.
 * @param unit - The unit to format the value to.
 * @param precision - The precision to format the value to.
 * @returns The formatted value.
 */
export const formatValue = (
  value: number,
  unit: string | Unit = '',
  precision = DEFAULT_PRECISION,
): string => {
  if (typeof unit === 'string') {
    const config = formatterOptions.includes(unit as FormatterType)
      ? {
          type: unit as FormatterType,
          precision,
        }
      : {
          type: 'customUnit' as FormatterType,
          unit,
          precision,
        };

    return formatByType(value, config);
  } else {
    return format(value, { input: unit, maximumFractionDigits: precision });
  }
};

/**
 * Computes the minimum precision needed so there's no repeated ticks after formatting.
 *
 * @returns number between DEFAULT_PRECISION and MAX_PRECISION_ALLOWED
 */
export function getFormatterPrecision(
  tickValues: number[],
  formatter: FormatterWithPrecision,
  unit?: string | Unit,
) {
  for (
    let minPrecision = DEFAULT_PRECISION;
    minPrecision <= MAX_PRECISION_ALLOWED;
    minPrecision++
  ) {
    const formattedTicks = tickValues.map((value) =>
      formatter(value, unit, minPrecision),
    );
    if (allUniq(formattedTicks)) {
      return minPrecision;
    }
  }
  return MAX_PRECISION_ALLOWED;
}

const allUniq = (arr: string[]) => uniq(arr).length === arr.length;

/**
 * Determines the type of value it is and formats accordingly.
 *
 * @param value - The value of the tick.
 * @param unit - The series unit.
 * @returns The correct format for the value that has been input.
 */
export const defaultTickValueFormatter = (
  value: TickPrimitiveValue,
  unit?: string | Unit,
) => {
  if (typeof value === 'string') {
    return `${value}${isEmpty(unit) ? '' : ` ${unit}`}`;
  }

  if (value instanceof Date) {
    return formatDateTicks(value);
  }

  return formatValue(value, unit, DEFAULT_PRECISION);
};

/**
 * Determines the type of value it is and formats accordingly
 *
 * @param tickFormatter - The type of formatter received based on whether scale is logarithmic or not.
 * @param unit - The series unit.
 * @returns The formatter to be applied, whether tickFormatter or the default.
 */
export const tickValueFormatter: (
  tickFormatter?: Formatter,
  unit?: string | Unit,
) => TickValueFormatter =
  (tickFormatter?: Formatter, unit?: string | Unit) =>
  (value: TickPrimitiveValue) =>
    tickFormatter && typeof value === 'number'
      ? tickFormatter(value, unit)
      : defaultTickValueFormatter(value, unit);

export const getTickValues = (
  originalTicks: TickPrimitiveValue[],
  formatter?: TickValueFormatter,
): string[] =>
  originalTicks.map((value) => formatter?.(value) ?? value.toString());
